import {Avatar, Modal} from 'antd'
import {course_getCourseSubjects_Api} from 'apis'
import {getId} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import getTitle from 'helpers/getTitle'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {withProps} from 'recompose'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {ListHeaderWithLine} from 'views/AppLocals/Templates/items/ListHeader'
import {NullAsDefault, renderElse, renderIf} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {SlateRenderer} from 'views/SlateEditor'
import {convertFromString, isEmptyContent} from 'views/SlateEditor/functions'
import {FetchSubjectById} from '../../components/FetchEntity'
import CourseContext from '../../CourseContext'
import {HeaderWithIconLine} from "../../../Templates/items/ListHeader";
import {RiSubscript} from "react-icons/ri";
import {GoDotFill} from "react-icons/go";
import {GiIronCross} from "react-icons/gi";
import useAsync from "../../../../../modules/asyncCache/useAsync";
import _ from "lodash";
import {getResponseItem} from "../../../../Shared";
import {TbListDetails} from "react-icons/tb";
import {BsInfoCircleFill} from "react-icons/bs";
import {ItemInformation} from "../../../Organization/OrganizationEntity";
import LoadingBlock from "../../../../LoadingBlock";

const renderItem = ({renderActions = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    const {subject} = item ?? {}
    const {description} = subject ?? {}

    return (
        <Translate key={index}>
            {(t) => (
                <div
                    onClick={() => {handleAction(CRUD.READ, item)}}
                    className="p-2 flex flex-col hover:shadow-md rounded-xl relative">
                    <div className="flex items-center gap-2 cursor-pointer">
                        <GiIronCross size={12} className="text-primary-300"/>
                        <span className="font-bold text-base text-color-000 leading-snug">
                            {getTitle(subject)}
                        </span>
                    </div>
                    {renderIf(
                        description,
                        <div className="ml-5 mb-1 text-xs text-color-300 tracking-wider max-lines-2">
                            {description}
                        </div>
                    )}
                    <div className="ml-5 flex items-center gap-1">
                        <GoDotFill size={10} className="text-color-400"/>
                        <span className="text-xs text-color-500 italic">
                            {`${t('number of sessions')} : `}
                        </span>
                        <span className="font-medium text-xs text-blue-600 italic">
                            {`${item?.number_of_sessions} ${t('sessions')}`}
                        </span>
                        <div className="flex-1"/>
                        {renderActions(item)}
                    </div>
                </div>
            )}
        </Translate>
    )
}

const DetailModalContent = ({item}) => {
    const t = useTranslate()
    const {
        about,
        description,
        number_of_sessions,
        subject_idcode,
        is_mandatory = 1,
    } = item ?? {}

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <Avatar
                    size={50}
                    icon={<RiSubscript size={26} className="text-primary-500"/>}
                    className="flex flex-center bg-primary-50 rounded-lg 222-222"
                />
                <div className="flex flex-col">
                    <span className="font-bold text-lg text-color-000">
                        {getTitle(item)}
                    </span>
                    <ItemInformation label="subject code" value={`#${subject_idcode}`} className="text-xs italic"/>
                </div>
            </div>
            {description && (
                <div className="text-color-300 tracking-wider">{description}</div>
            )}
            <div className="flex flex-col gap-2">
                <HeaderWithIconLine
                    icon={<BsInfoCircleFill size={14} className="text-blue-400 mr-1"/>}
                    title="subject information"
                    className="text-sm text-color-400"
                />
                <div className="ml-0 md:ml-1 flex flex-col flex-1 gap-1">
                    <ItemInformation
                        showIcon={true}
                        label="number of sessions"
                        value={`${number_of_sessions} ${t('sessions')}`}
                        className="text-sm"
                    />
                    {renderElse(
                        isEmptyContent(about),
                        <div className="flex flex-col">
                            <span className="text-sm text-color-500 italic">
                                {`${t('introduce')} : `}
                            </span>
                            <SlateRenderer value={convertFromString(about)}/>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const useCourseSubjects = ({course}) => {
    const t = useTranslate()

    const {edit} = course ?? {}

    const course_id = getId(course)

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onCancel = () => {
        setAction({})
    }

    const modal = useMemo(() => {
        const {id} = action?.value ?? {}
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({item: {}})
        let params = {}
        let modal_params = {}
        switch (action?.type) {
            case CRUD.READ:
                modal_params = {
                    width: 600,
                    footer: undefined,
                    cancelText: t('close'),
                    okButtonProps: {hidden: true},
                    cancelButtonProps: {
                        className: 'text-color-200 button-rounded-lg no-border',
                    },
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('subjects')}
                        </div>
                    ),
                }
                params = {item: action?.value}
                Content = DetailModalContent
                Wrapper = withProps({id})(FetchSubjectById)
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                {...{item: action?.value, onCancel, course_id}}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action, course_id])

    const renderActions = useCallback(
        (item) => (
            <div
                onClick={() => {
                    setAction({type: CRUD.READ, value: item?.subject})
                }}
                className="text-xs text-color-400 hover:text-color-100 italic cursor-pointer">
                {`${t('quick view')} ...`}
            </div>
        ),
        []
    )

    return {
        modal,
        action,
        onCancel,
        setAction,
        course_id,
        renderActions,
    }
}

const SubjectList = ({item, ...props}) => {
    const t = useTranslate()
    const course_id = getId(item)

    const apiInfo = course_getCourseSubjects_Api

    const refreshKey = useMemo(
        () => [apiInfo.path.replace(':id', course_id)].join('/'),
        [course_id]
    )

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const {response, isLoading = false} = useAsync({
        apiInfo,
        query: bindQueryParam({id: course_id}),
    });

    const isSucceed = Number(_.get(response, 'status')) === 200

    const cache = getResponseItem(response)

    const error = getResponseItem(response, 'error')

    const errorMessage = _.get(response, 'errorMessages.error')

    const error_code = _.get(response, 'errorMessages.error_code')

    const status = {
        title: getResponseItem(response, 'status'),
        description: errorMessage,
    }

    const data = _.get(cache, 'data', [])
    const groupedData = _.groupBy(data, 'group_name');
    const groupedDataKeysNotSorted = Object.keys(groupedData);
    // Lọc ra các phần tử null
    const nullElements = _.filter(groupedDataKeysNotSorted, element => element === null);
    // Lọc ra các phần tử không phải null
    const nonNullElements = _.filter(groupedDataKeysNotSorted, element => element !== null);
    // Sắp xếp các phần tử không phải null
    const sortedNonNullElements = _.sortBy(nonNullElements, [element => element.toString().toLowerCase()]);
    // Gộp các phần tử null với các phần tử đã sắp xếp
    const groupedDataKeys = [...nullElements, ...sortedNonNullElements];

    const {modal, renderActions} = useCourseSubjects({
        course: item,
    })

    if (data.length <= 0) return null;

    return (
        <>
            <div className="flex flex-col items-start space-y-6">
                <div className="w-full flex flex-col flex-1 gap-4">
                    <HeaderWithIconLine
                        icon={<RiSubscript size={18} className="text-color-500"/>}
                        title="list of subjects"
                        className="text-lg text-color-400"
                    />
                    <div style={{marginLeft: 10}} className="flex flex-col gap-4">
                        {groupedDataKeys.map((e, i) => {
                            return (
                                <div key={i} className="w-full flex flex-col flex-1 gap-3">
                                    {renderIf(
                                        e !== 'null' && groupedDataKeys.length > 1,
                                        <div
                                            style={{
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                background: 'linear-gradient(to right, var(--background-200), rgba(0, 0, 0, 0) 100%)'
                                            }}
                                            className="w-90 flex items-center gap-1">
                                            <TbListDetails className="text-color-300"/>
                                            <span
                                                className="font-semibold text-sm text-color-300 uppercase tracking-wider">
                                                {`  -  ${e}`}
                                            </span>
                                        </div>
                                    )}
                                    <div className="space-y-4">
                                        {_.sortBy(_.get(groupedData, e, []), ['subject.title']).map((ee, ii) => {
                                            return renderItem({
                                                renderActions,
                                                handleAction: (type, value) => {
                                                    setAction({type, value});
                                                }
                                            })(ee, ii)
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
            {modal}
        </>
    )

}

const CourseSubjectTab = (props) => {
    const {item} = useContext(CourseContext)
    return (
        <SubjectList
            item={item}
            {...props}
        />
    )
}

export const CourseSubjectShortList = (props) => {
    const {item} = useContext(CourseContext)
    return (
        <SubjectList
            item={item}
            sample={2}
            {...props}
        />
    )
}

export const CourseSubjectsInfo = ({course}) => {
    const subjects = Array.from(course?.subjects ?? [])
    const {modal, renderActions} = useCourseSubjects({
        course,
    })
    return (
        <div className="flex flex-col items-start space-y-6">
            <div className="w-full flex flex-col flex-1 gap-4">
                <ListHeaderWithLine title="list of subjects"/>
                <div className="flex flex-col gap-3">
                    {subjects.length > 0 ? (
                        subjects.map(
                            renderItem({
                                renderActions,
                            })
                        )
                    ) : (
                        <EmptyHolder/>
                    )}
                </div>
            </div>
            {modal}
        </div>
    )
}

export default CourseSubjectTab
